import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Paper
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Close as CloseIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Delete as DeleteIcon,
    InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { useSnackbar } from '../../hooks/useSnackbar';
import {
    FileType,
    uploadWorkerFile,
    formatFileSize,
    FILE_TYPE_LABELS,
} from '../../api/workerFilesApi';
import { ingestCertFromWorkerFile } from '../../api/workerCertificationsApi';

interface FileUploadModalProps {
    open: boolean;
    onClose: () => void;
    workerId: string;
    onSuccess: () => void;
    preselectedFileType?: FileType | null;
}

interface FileToUpload {
    file: File;
    type: FileType;
    id: string;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
    open,
    onClose,
    workerId,
    onSuccess,
    preselectedFileType
}) => {
    const [files, setFiles] = useState<FileToUpload[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentUploadName, setCurrentUploadName] = useState<string>('');
    const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
    const [uploadResults, setUploadResults] = useState<Array<{
        fileName: string;
        success: boolean;
        error?: string;
    }>>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showSnackbar } = useSnackbar();

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles) return;

        const newFiles: FileToUpload[] = Array.from(selectedFiles).map(file => ({
            file,
            type: preselectedFileType || detectFileType(file.name),
            id: `${file.name}-${Date.now()}-${Math.random()}`
        }));

        setFiles(prev => [...prev, ...newFiles]);
        
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const detectFileType = (filename: string): FileType => {
        const lowercaseFilename = filename.toLowerCase();

        if (lowercaseFilename.includes('rijbewijs') ||
            (lowercaseFilename.includes('driver') && (lowercaseFilename.includes('license') || lowercaseFilename.includes('licence'))) ||
            lowercaseFilename.includes('drivinglicence')) {
            return FileType.RIJBEWIJS;
        }

        if (lowercaseFilename.includes('passport') ||
            lowercaseFilename.includes('id-card') ||
            lowercaseFilename.includes('idcard') ||
            lowercaseFilename.includes('identiteitskaart') ||
            lowercaseFilename.includes('identity') ||
            lowercaseFilename.includes('residence') ||
            lowercaseFilename.includes('permit')) {
            return FileType.ID_DOCUMENT;
        }

        if (lowercaseFilename.includes('cv') || lowercaseFilename.includes('resume')) {
            return FileType.CV;
        }

        if (lowercaseFilename.includes('cert') || lowercaseFilename.includes('certificate')) {
            return FileType.CERTIFICATION;
        }

        return FileType.OTHER;
    };

    const handleTypeChange = (fileId: string, newType: FileType) => {
        setFiles(prev => prev.map(f => 
            f.id === fileId ? { ...f, type: newType } : f
        ));
    };

    const handleRemoveFile = (fileId: string) => {
        setFiles(prev => prev.filter(f => f.id !== fileId));
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            showSnackbar('Please select files to upload', 'warning');
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setCurrentUploadIndex(0);
        setCurrentUploadName('');
        setUploadResults([]);

        const results: typeof uploadResults = [];
        const totalFiles = files.length;

        for (let i = 0; i < files.length; i++) {
            const fileToUpload = files[i];
            setCurrentUploadIndex(i);
            setCurrentUploadName(fileToUpload.file.name);
            // Reset to where this file starts in the overall transfer so
            // multi-file progress doesn't snap backwards mid-upload.
            setUploadProgress((i / totalFiles) * 100);

            try {
                const saved = await uploadWorkerFile(
                    {
                        workerId,
                        file: fileToUpload.file,
                        fileType: fileToUpload.type,
                    },
                    (filePercent) => {
                        setUploadProgress(((i + filePercent / 100) / totalFiles) * 100);
                    },
                );

                // CERTIFICATION uploads also enter the cert pipeline so they
                // appear in the review queue + worker cert section. We fire
                // and forget — if cert ingest fails (e.g. unsupported format),
                // the worker_file still saved successfully, so the upload itself
                // isn't considered a failure.
                if (fileToUpload.type === FileType.CERTIFICATION) {
                    try {
                        await ingestCertFromWorkerFile(saved.id);
                    } catch (certErr: any) {
                        // eslint-disable-next-line no-console
                        console.warn(
                            `Cert ingest failed for ${fileToUpload.file.name}:`,
                            certErr?.response?.data?.message ?? certErr?.message,
                        );
                    }
                }

                results.push({
                    fileName: fileToUpload.file.name,
                    success: true
                });
            } catch (error: any) {
                results.push({
                    fileName: fileToUpload.file.name,
                    success: false,
                    error: error.response?.data?.message || error.message || 'Upload failed'
                });
            }
        }

        setUploadResults(results);
        setUploading(false);

        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;

        if (successCount > 0) {
            showSnackbar(
                `Successfully uploaded ${successCount} file(s)${failCount > 0 ? `, ${failCount} failed` : ''}`,
                failCount > 0 ? 'warning' : 'success'
            );
            onSuccess();
        } else {
            showSnackbar('All uploads failed', 'error');
        }
    };

    const handleClose = () => {
        if (!uploading) {
            setFiles([]);
            setUploadResults([]);
            onClose();
        }
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        const droppedFiles = event.dataTransfer.files;
        
        if (droppedFiles) {
            const newFiles: FileToUpload[] = Array.from(droppedFiles).map(file => ({
                file,
                type: preselectedFileType || detectFileType(file.name),
                id: `${file.name}-${Date.now()}-${Math.random()}`
            }));

            setFiles(prev => [...prev, ...newFiles]);
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {preselectedFileType === FileType.PROFILE_PICTURE ? 'Upload Profile Picture' :
                         preselectedFileType === FileType.WORK_PICTURE ? 'Upload Work Picture' :
                         'Upload Files'}
                    </Typography>
                    <IconButton onClick={handleClose} disabled={uploading}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                {!uploading && uploadResults.length === 0 && (
                    <>
                        <Paper
                            sx={{
                                p: 4,
                                mb: 2,
                                textAlign: 'center',
                                backgroundColor: 'action.hover',
                                border: '2px dashed',
                                borderColor: 'divider',
                                cursor: 'pointer'
                            }}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                            <Typography variant="h6" gutterBottom>
                                Drop files here or click to browse
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Supported formats: PDF, Word, Images
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Maximum file size: 10MB
                            </Typography>
                        </Paper>

                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple={preselectedFileType !== FileType.PROFILE_PICTURE}
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                            accept={preselectedFileType === FileType.PROFILE_PICTURE || preselectedFileType === FileType.WORK_PICTURE 
                                ? ".jpg,.jpeg,.png,.gif,.webp" 
                                : ".pdf,.doc,.docx,.jpg,.jpeg,.png"}
                        />

                        {files.length > 0 && (
                            <List>
                                {files.map((fileItem) => (
                                    <ListItem
                                        key={fileItem.id}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                onClick={() => handleRemoveFile(fileItem.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon>
                                            <FileIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={fileItem.file.name}
                                            secondary={formatFileSize(fileItem.file.size)}
                                        />
                                        <FormControl size="small" sx={{ minWidth: 150, mr: 2 }}>
                                            <Select
                                                value={fileItem.type}
                                                onChange={(e) => handleTypeChange(fileItem.id, e.target.value as FileType)}
                                            >
                                                {[FileType.CV, FileType.CERTIFICATION, FileType.RIJBEWIJS, FileType.ID_DOCUMENT, FileType.PROFILE_PICTURE, FileType.WORK_PICTURE, FileType.REFERENCE, FileType.OTHER].map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {FILE_TYPE_LABELS[type] ?? type}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </>
                )}

                {uploading && (
                    <Box>
                        <Typography variant="body1" gutterBottom>
                            {files.length > 1
                                ? `Uploading file ${currentUploadIndex + 1} of ${files.length}: ${currentUploadName}`
                                : `Uploading ${currentUploadName}`}
                        </Typography>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                            {Math.round(uploadProgress)}% complete
                        </Typography>
                    </Box>
                )}

                {uploadResults.length > 0 && (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Upload Results
                        </Typography>
                        <List>
                            {uploadResults.map((result, index) => (
                                <ListItem key={index}>
                                    <ListItemIcon>
                                        {result.success ? (
                                            <SuccessIcon color="success" />
                                        ) : (
                                            <ErrorIcon color="error" />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={result.fileName}
                                        secondary={result.error}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {uploadResults.length === 0 && (
                    <>
                        <Button onClick={handleClose} disabled={uploading}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleUpload}
                            disabled={uploading || files.length === 0}
                            startIcon={<UploadIcon />}
                        >
                            Upload {files.length > 0 && `(${files.length})`}
                        </Button>
                    </>
                )}
                {uploadResults.length > 0 && (
                    <Button onClick={handleClose} variant="contained">
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default FileUploadModal;
